// company-manifest.service.ts
import {computed, Injectable, resource, signal} from '@angular/core';
import {StorageKeys} from '@core/storage';
import {localStorageSignal} from '@ddtmm/angular-signal-generators';
import {companyManifestFactory, environment, ICompanyManifest} from '@shared-types';

const LOCAL_COMPANY_MANIFEST$ = localStorageSignal<ICompanyManifest>(undefined, StorageKeys.GlobalKeys.COMPANY_MANIFEST);

@Injectable({
	providedIn: 'root',
})
export class CompanyManifestService {
	private readonly MANIFEST_BASE_URL = 'https://s3.eu-central-1.amazonaws.com/sps.media/doccycle/manifest/{{companyId}}.manifest.json' as const;
	private readonly companyId$ = signal<string>(undefined);
	private companyManifestResource$ = resource({
		request: () => ({companyId: this.companyId$()}),
		loader: async ({request}) => {
			const {companyId} = request;
			if (!companyId) {
				// skip if companyId is not set
				return undefined;
			}
			try {
				// Construct the URL using the companyId
				const url = this.MANIFEST_BASE_URL.replace('{{companyId}}', companyId);
				// Fetch the company manifest from the constructed URL
				const response = await fetch(url, {
					headers: {
						Method: 'GET',
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache',
						Expires: '0',
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				});
				if (!response.ok) {
					return undefined;
				}
				const responseJson = (await response.json()) as ICompanyManifest;
				return companyManifestFactory(responseJson);
			} catch (error) {
				console.error(`Error fetching company manifest for ID: ${companyId}`, error);
				return undefined;
			}
		},
		defaultValue: LOCAL_COMPANY_MANIFEST$(),
	});

	public readonly companyManifest$ = computed(() => this.companyManifestResource$.value());

	constructor() {
		this.companyId$.set(environment.company);
	}
}
